/**
 * Portfolio Overview
 *
 * Cross-asset summary page: system controls, engine health,
 * all positions, and quick stats.
 */

import React, { useState, useEffect } from 'react';
import { TrendingUp, Crosshair, ListTodo } from 'lucide-react';

import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

import { useTitanStore } from '../stores/titanStore';
import ControlPanel from '../components/titan/ControlPanel';
import PositionMonitor from '../components/titan/PositionMonitor';
import OrderHistory from '../components/titan/OrderHistory';
import AssetSessionPanel from '../components/titan/AssetSessionPanel';
import ExitProtectionPanel from '../components/titan/ExitProtectionPanel';
import { API_BASE } from '../lib/api';
import { UI_CANONICAL_ORIGIN } from '../config/site';
import { useEquitiesMode } from '../hooks/useEquitiesMode';
import { filterStockPatterns } from '../lib/stocksOnly';

const PortfolioOverview: React.FC = () => {
  const { equities_only: equitiesOnly } = useEquitiesMode();
  const [tabValue, setTabValue] = useState('positions');

  const {
    systemStatus,
    systemStatusError,
    patternsError,
    fetchSystemStatus,
    fetchPatterns,
    patterns,
  } = useTitanStore();

  useEffect(() => {
    fetchSystemStatus();
    fetchPatterns();
    const interval = setInterval(fetchSystemStatus, 12000);
    return () => clearInterval(interval);
  }, [fetchSystemStatus, fetchPatterns]);

  const activePatterns = systemStatus?.active_patterns || 0;
  const brokerPositions =
    systemStatus?.broker_open_positions ?? systemStatus?.open_positions ?? 0;
  const managedPositions = systemStatus?.managed_open_positions;
  const externalPositions = systemStatus?.external_open_positions ?? 0;

  const stockPatterns = filterStockPatterns(patterns, equitiesOnly);
  const stockBots = stockPatterns.length;
  const activeStockBots = stockPatterns.filter((p) => p.is_active).length;

  return (
    <div className="space-y-4 md:space-y-6">
      {(systemStatusError || patternsError) && (
        <Alert variant="destructive">
          <AlertTitle>Unable to load live data</AlertTitle>
          <AlertDescription className="space-y-1 text-sm">
            {systemStatusError && <p>System status: {systemStatusError}</p>}
            {patternsError && <p>Patterns: {patternsError}</p>}
            <p className="text-xs opacity-90">
              The dashboard UI is served from your hosting domain (production:{' '}
              <span className="font-mono text-xs">{UI_CANONICAL_ORIGIN}</span>
              ); API calls go to a separate host:{' '}
              <span className="font-mono text-xs">{API_BASE || 'https://api.titan-trading.live'}</span>.
              Sign in, confirm credentials, then verify that API host is reachable (VPN/firewall/DNS) and that GCP is not returning 503/429 on the API domain without CORS headers.
            </p>
          </AlertDescription>
        </Alert>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <TrendingUp className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground">Portfolio Overview</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge variant="default" className="text-xs">System Managed</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-4 md:pt-6">
            <div className="space-y-1">
              <p className="text-xs md:text-sm text-muted-foreground">Active Bots</p>
              <p className="text-xl md:text-2xl font-bold text-foreground">{activePatterns}</p>
              <p className="text-xs text-muted-foreground">of {stockBots} stock bots</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 md:pt-6">
            <div className="space-y-1">
              <p className="text-xs md:text-sm text-muted-foreground">Open Positions (broker)</p>
              <p className="text-xl md:text-2xl font-bold text-foreground">{brokerPositions}</p>
              {managedPositions != null && (
                <p className="text-xs text-muted-foreground">System-managed: {managedPositions}</p>
              )}
              {externalPositions > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  External/manual: {externalPositions}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 md:pt-6">
            <div className="space-y-1">
              <p className="text-xs md:text-sm text-muted-foreground">Stock Bots</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {stockBots > 0 ? (
                  <Badge variant="secondary" className="text-xs">
                    {activeStockBots} active / {stockBots} total
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground">None</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 md:pt-6">
            <div className="space-y-1">
              <p className="text-xs md:text-sm text-muted-foreground">Evaluations</p>
              <p className="text-xl md:text-2xl font-bold text-foreground">{systemStatus?.pattern_engine?.total_evaluations || 0}</p>
              <p className="text-xs text-muted-foreground">{systemStatus?.pattern_engine?.total_signals || 0} signals</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Session Goals — per asset class with profit targets and loss limits */}
      <AssetSessionPanel />

      <ExitProtectionPanel />

      {/* Control Panel */}
      <div>
        <ControlPanel />
      </div>

      {/* Tabs: Positions | Orders */}
      <Tabs value={tabValue} onValueChange={setTabValue}>
        <TabsList>
          <TabsTrigger value="positions" className="gap-2">
            <Crosshair className="h-4 w-4" />
            Positions{brokerPositions > 0 ? ` (${brokerPositions})` : ''}
          </TabsTrigger>
          <TabsTrigger value="orders" className="gap-2">
            <ListTodo className="h-4 w-4" />
            Orders
          </TabsTrigger>
        </TabsList>

        <TabsContent value="positions">
          <PositionMonitor />
        </TabsContent>

        <TabsContent value="orders">
          <OrderHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PortfolioOverview;
